import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MINIMUM_ORDER = 1000;
const DELHI_NCR_CITIES = ["delhi", "new delhi", "noida", "greater noida", "gurugram", "gurgaon", "faridabad", "ghaziabad"];

function normalizeCity(city) {
  return city.trim().toLowerCase().replace(/[^a-z\s]/g, "").replace(/\s+/g, " ");
}

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", address: "", city: "", pincode: "", delivery_note: "", upi_reference: "" });
  const [orderError, setOrderError] = useState("");
  const [deliveryConfirmed, setDeliveryConfirmed] = useState(false);
  const reduceMotion = useReducedMotion();
  const cityNormalized = normalizeCity(form.city);
  const cityEntered = cityNormalized.length > 0;
  const isDelhiNcrCity = DELHI_NCR_CITIES.includes(cityNormalized);

  if ((!items.length || subtotal < MINIMUM_ORDER) && !order) return <Navigate to="/cart" replace />;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const requiredDetails = ["full_name", "email", "phone", "city", "pincode", "address"];
  const detailsComplete = requiredDetails.every((field) => form[field].trim().length > 0);

  const continueToPayment = (event) => {
    event.preventDefault();
    setOrderError("");
    if (!detailsComplete) {
      setOrderError("Please fill in all your delivery details before continuing.");
      return;
    }
    if (!deliveryConfirmed) {
      setOrderError("Please confirm that your delivery address is in Delhi NCR before continuing.");
      return;
    }
    if (!isDelhiNcrCity) {
      setOrderError("We currently deliver only in Delhi NCR. Please do not proceed for addresses outside Delhi NCR.");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const backToDetails = () => {
    setOrderError("");
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitPayment = async (event) => {
    event.preventDefault();
    setOrderError("");
    if (!form.upi_reference.trim()) {
      setOrderError("Please enter the UPI transaction/reference ID after paying.");
      return;
    }
    const payload = {
      items: items.map((item) => ({ product_id: item.id, name: item.name, quantity: item.quantity, pack_size: item.packSize, unit_price: item.price })),
      customer: {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        city: form.city,
        pincode: form.pincode,
        delivery_note: form.delivery_note,
      },
      subtotal,
      payment_method: "UPI QR manual payment",
      payment_reference: form.upi_reference,
    };
    try {
      const response = await axios.post(`${API}/orders`, payload);
      setOrder(response.data);
      clearCart();
    } catch (error) {
      setOrderError(error?.response?.data?.detail || "Unable to submit order details. Please check the payment reference and try again.");
    }
  };

  if (order) {
    return (
      <main className="bg-[#FAFAF8] pt-32" data-testid="order-confirmation-page">
        <section className="mx-auto max-w-3xl px-6 pb-24 text-center md:px-10 md:pb-32">
          <motion.div initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 18 }} animate={reduceMotion ? {} : { opacity: 1, scale: 1, y: 0 }} className="rounded-[2.25rem] bg-white p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <motion.div initial={reduceMotion ? false : { scale: 0.5, rotate: -12 }} animate={reduceMotion ? {} : { scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 220, damping: 16 }}><CheckCircle2 className="mx-auto h-14 w-14 text-[#4F795B]" /></motion.div>
            <h1 data-testid="order-confirmation-heading" className="mt-7 text-5xl font-light tracking-tight text-[#2C2C2A]">Harvest request received.</h1>
            <p data-testid="order-confirmation-id" className="mt-5 text-[#6B6B66]">Order ID: {order.id}</p>
            <p data-testid="order-confirmation-payment-note" className="mt-4 text-lg leading-relaxed text-[#6B6B66]">Payment received for manual verification. Tatva Hills will confirm the UPI reference, availability and delivery timing.</p>
            <p data-testid="order-payment-proof-note" className="mt-4 rounded-[1.25rem] bg-[#F0F0EA] p-4 text-sm leading-relaxed text-[#4A3B32]">Please keep your UPI payment proof or screenshot safely until your order is confirmed.</p>
            <Link data-testid="confirmation-shop-link" to="/shop" className="mt-8 inline-flex rounded-full bg-[#2A4B3C] px-8 py-4 font-medium text-white">Continue Shopping</Link>
          </motion.div>
        </section>
      </main>
    );
  }

  return (
    <main className="bg-[#FAFAF8] pt-32" data-testid="checkout-page">
      <section className="mx-auto grid max-w-7xl gap-8 px-6 pb-24 md:px-10 md:pb-32 lg:grid-cols-[1fr_380px]">
        <div>
          {/* Step indicator */}
          <div data-testid="checkout-step-indicator" className="mb-7 flex items-center gap-3 text-sm">
            <span data-testid="step-indicator-1" className={`flex items-center gap-2 rounded-full px-4 py-2 font-medium ${step === 1 ? "bg-[#2A4B3C] text-white" : "bg-white text-[#6B6B66]"}`}>
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${step === 1 ? "bg-white text-[#2A4B3C]" : "bg-[#EAEAE6] text-[#6B6B66]"}`}>1</span>
              Your Details
            </span>
            <span className="h-px w-8 bg-[#D5D5D0]" />
            <span data-testid="step-indicator-2" className={`flex items-center gap-2 rounded-full px-4 py-2 font-medium ${step === 2 ? "bg-[#2A4B3C] text-white" : "bg-white text-[#6B6B66]"}`}>
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${step === 2 ? "bg-white text-[#2A4B3C]" : "bg-[#EAEAE6] text-[#6B6B66]"}`}>2</span>
              Payment
            </span>
          </div>

          {step === 1 && (
            <motion.form key="step-1" initial={reduceMotion ? false : { opacity: 0, y: 20 }} animate={reduceMotion ? {} : { opacity: 1, y: 0 }} data-testid="checkout-details-form" onSubmit={continueToPayment} className="rounded-[2rem] bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <h1 data-testid="checkout-heading" className="text-5xl font-light tracking-tight text-[#2C2C2A]">Delivery details</h1>
              <div data-testid="checkout-delivery-zone-alert" className="mt-6 rounded-[1.5rem] border-2 border-[#C84B31] bg-[#FFF4EF] p-5 text-[#C84B31]">
                <div className="flex gap-3">
                  <AlertTriangle className="mt-1 h-6 w-6 shrink-0" />
                  <div>
                    <p className="text-lg font-semibold">DELIVERY ONLY IN DELHI NCR</p>
                    <p className="mt-2 text-sm leading-relaxed">We can deliver only in Delhi NCR. Supported areas: Delhi, New Delhi, Noida, Greater Noida, Gurugram/Gurgaon, Faridabad and Ghaziabad. Please do not pay for addresses outside Delhi NCR.</p>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-[#6B6B66]">Enter your delivery details and confirm your city is covered. You'll pay via UPI QR on the next step.</p>
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {[
                  ["full_name", "Full name"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["city", "City"],
                  ["pincode", "Pincode"],
                ].map(([field, label]) => (
                  <label key={field} className="text-sm font-medium text-[#4A3B32]">{label}
                    <input data-testid={`checkout-${field.replaceAll("_", "-")}-input`} required value={form[field]} onChange={(event) => update(field, event.target.value)} className="mt-2 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-4 outline-none" />
                  </label>
                ))}
                <label className="text-sm font-medium text-[#4A3B32] md:col-span-2">Address
                  <textarea data-testid="checkout-address-input" required value={form.address} onChange={(event) => update("address", event.target.value)} className="mt-2 min-h-28 w-full rounded-[1.5rem] border border-[#D5D5D0] bg-[#FAFAF8] p-4 outline-none" />
                </label>
                <label className="text-sm font-medium text-[#4A3B32] md:col-span-2">Delivery note
                  <textarea data-testid="checkout-delivery-note-input" value={form.delivery_note} onChange={(event) => update("delivery_note", event.target.value)} className="mt-2 min-h-24 w-full rounded-[1.5rem] border border-[#D5D5D0] bg-[#FAFAF8] p-4 outline-none" />
                </label>
              </div>
              {cityEntered && !isDelhiNcrCity && <p data-testid="checkout-city-not-covered-warning" className="mt-5 rounded-[1.25rem] bg-[#FFF4EF] p-4 text-sm font-semibold text-[#C84B31]">We currently deliver only in Delhi NCR. Please do not make payment for this city.</p>}
              <label data-testid="delivery-confirmation-checkbox-label" className="mt-6 flex cursor-pointer items-start gap-3 rounded-[1rem] border-2 border-[#C84B31] bg-[#FFF4EF] p-4 text-sm leading-relaxed text-[#4A3B32]">
                <input data-testid="delivery-confirmation-checkbox" type="checkbox" checked={deliveryConfirmed} onChange={(event) => setDeliveryConfirmed(event.target.checked)} className="mt-1 h-4 w-4 accent-[#2A4B3C]" />
                <span>I confirm my delivery address is in Delhi NCR and I will pay only after confirming this.</span>
              </label>
              {orderError && <p data-testid="checkout-error-message" className="mt-5 rounded-[1.25rem] bg-[#FFF4EF] p-4 text-sm font-medium text-[#C84B31]">{orderError}</p>}
              <button data-testid="checkout-continue-button" type="submit" disabled={!deliveryConfirmed || !isDelhiNcrCity} className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 font-medium transition-transform duration-300 ${deliveryConfirmed && isDelhiNcrCity ? "bg-[#2A4B3C] text-white hover:-translate-y-1" : "cursor-not-allowed bg-[#D5D5D0] text-[#6B6B66]"}`}>Continue to Payment <ArrowRight className="h-4 w-4" /></button>
            </motion.form>
          )}

          {step === 2 && (
            <motion.form key="step-2" initial={reduceMotion ? false : { opacity: 0, y: 20 }} animate={reduceMotion ? {} : { opacity: 1, y: 0 }} data-testid="checkout-payment-form" onSubmit={submitPayment} className="rounded-[2rem] bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <button data-testid="back-to-details-button" type="button" onClick={backToDetails} className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-[#6B6B66] hover:text-[#2A4B3C]"><ArrowLeft className="h-4 w-4" /> Back to details</button>
              <h1 data-testid="payment-heading" className="text-5xl font-light tracking-tight text-[#2C2C2A]">Pay with UPI</h1>
              <p className="mt-4 text-[#6B6B66]">Scan the QR below, pay exactly <strong className="text-[#2C2C2A]">₹{subtotal}</strong>, then enter your UPI transaction/reference ID to place the order.</p>

              <div data-testid="payment-delivery-summary" className="mt-6 rounded-[1.25rem] bg-[#F0F0EA] p-5 text-sm leading-relaxed text-[#4A3B32]">
                Delivering to <strong>{form.full_name}</strong>, {form.address}, {form.city} {form.pincode}.
              </div>

              <div className="mt-7 flex flex-col items-center rounded-[1.5rem] border border-[#EAEAE6] bg-[#FAFAF8] p-6">
                <p data-testid="payment-amount" className="text-sm font-medium text-[#6B6B66]">Amount to pay</p>
                <p className="text-4xl font-light text-[#2C2C2A]">₹{subtotal}</p>
                <img data-testid="upi-payment-qr-image" src="/images/payments/axis-upi-qr.jpg" alt="Tatva Hills Axis Bank UPI QR code" className="mt-5 w-full max-w-xs rounded-[1rem] border border-[#EAEAE6] bg-white" />
                <p data-testid="upi-manual-verification-note" className="mt-4 text-center text-xs leading-relaxed text-[#6B6B66]">Orders are confirmed after manual payment verification. Please keep your payment screenshot.</p>
              </div>

              <label className="mt-7 block text-sm font-medium text-[#4A3B32]">UPI transaction/reference ID
                <input data-testid="checkout-upi-reference-input" required value={form.upi_reference} onChange={(event) => update("upi_reference", event.target.value)} placeholder="Example: UPI/IMPS reference number" className="mt-2 h-12 w-full rounded-full border border-[#D5D5D0] bg-[#FAFAF8] px-4 outline-none" />
              </label>
              {orderError && <p data-testid="checkout-error-message" className="mt-5 rounded-[1.25rem] bg-[#FFF4EF] p-4 text-sm font-medium text-[#C84B31]">{orderError}</p>}
              <button data-testid="checkout-submit-button" type="submit" className="mt-8 inline-flex w-full justify-center rounded-full bg-[#2A4B3C] px-8 py-4 font-medium text-white transition-transform duration-300 hover:-translate-y-1">Submit Paid Order Details</button>
            </motion.form>
          )}
        </div>

        <aside data-testid="checkout-summary" className="h-fit rounded-[2rem] bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h2 className="text-2xl font-normal text-[#2C2C2A]">Your harvest</h2>
          <div className="mt-6 space-y-4">
            {items.map((item) => <div data-testid={`checkout-summary-item-${item.slug}`} key={`${item.id}-${item.packSize}`} className="flex justify-between gap-4 text-sm"><span>{item.name} × {item.quantity}</span><strong>₹{item.price * item.quantity}</strong></div>)}
          </div>
          <div className="mt-6 flex justify-between border-t border-[#EAEAE6] pt-5"><span>Subtotal</span><strong data-testid="checkout-subtotal">₹{subtotal}</strong></div>
          <div data-testid="summary-delivery-note" className="mt-6 rounded-[1.25rem] bg-[#FFF4EF] p-4 text-xs font-medium leading-relaxed text-[#C84B31]">Delivery only in Delhi NCR. Pay via UPI QR — orders confirmed after manual verification.</div>
        </aside>
      </section>
    </main>
  );
}
